import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

import type { CollectionKind, CollectionNavigationContext } from "@/lib/collection-contact-navigation"

export type ContactReturnHighlightScope =
  | { type: "collection"; kind: CollectionKind }
  | { type: "list"; listId: string }
  | { type: "folder"; folderId: string }

export type ContactReturnHighlight = {
  contactId: string
  scope: ContactReturnHighlightScope
  /** 1-based list page to restore when returning (optional; destination may also derive from nav index). */
  page?: number
}

export type CollectionNavigationState = {
  /** Query scope for collection prev/next neighbor lookup. Persisted. */
  context: CollectionNavigationContext | null
  /** One-shot row highlight when returning from contact detail. Session-only. */
  returnHighlight: ContactReturnHighlight | null
}

const initialState: CollectionNavigationState = {
  context: null,
  returnHighlight: null,
}

const collectionNavigationSlice = createSlice({
  name: "collectionNavigation",
  initialState,
  reducers: {
    setCollectionNavigationContext(
      state,
      action: PayloadAction<CollectionNavigationContext>,
    ) {
      state.context = action.payload
    },
    clearCollectionNavigationContext(state) {
      state.context = null
    },
    setContactReturnHighlight(state, action: PayloadAction<ContactReturnHighlight>) {
      const contactId = action.payload.contactId?.trim()
      if (!contactId) {
        state.returnHighlight = null
        return
      }
      const page =
        typeof action.payload.page === "number" &&
        Number.isFinite(action.payload.page) &&
        action.payload.page >= 1
          ? Math.floor(action.payload.page)
          : undefined
      state.returnHighlight = {
        contactId,
        scope: action.payload.scope,
        ...(page != null ? { page } : {}),
      }
    },
    clearContactReturnHighlight(state) {
      state.returnHighlight = null
    },
  },
})

export const {
  setCollectionNavigationContext,
  clearCollectionNavigationContext,
  setContactReturnHighlight,
  clearContactReturnHighlight,
} = collectionNavigationSlice.actions

export const collectionNavigationReducer = collectionNavigationSlice.reducer

export function selectCollectionNavigationContext(state: {
  collectionNavigation: CollectionNavigationState
}): CollectionNavigationContext | null {
  return state.collectionNavigation.context
}

export function selectCollectionNavigationContextForKind(
  kind: CollectionKind,
): (state: { collectionNavigation: CollectionNavigationState }) => CollectionNavigationContext | null {
  return (state) => {
    const ctx = state.collectionNavigation.context
    if (!ctx || ctx.collection !== kind) return null
    return ctx
  }
}

export function selectContactReturnHighlight(state: {
  collectionNavigation: CollectionNavigationState
}): ContactReturnHighlight | null {
  return state.collectionNavigation.returnHighlight
}

export function contactReturnHighlightScopesMatch(
  pending: ContactReturnHighlightScope,
  page: ContactReturnHighlightScope,
): boolean {
  if (pending.type !== page.type) return false
  if (pending.type === "collection" && page.type === "collection") {
    return pending.kind === page.kind
  }
  if (pending.type === "list" && page.type === "list") {
    return pending.listId.trim() === page.listId.trim()
  }
  if (pending.type === "folder" && page.type === "folder") {
    return pending.folderId.trim() === page.folderId.trim()
  }
  return false
}
